import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { tendersAPI } from '../api/api';
import { toast } from '../hooks/use-toast';
import AppLayout from '../components/AppLayout';
import { Star, Building2, Briefcase, Calendar, ArrowRight, ListChecks } from 'lucide-react';

// ───────────────────────────────────────────────────────────────────────────
// Global shortlists view — every shortlisted (tender, consultant) pair across
// the org. Useful when staffing several AOs in parallel.
// Grouped by tender for readability.
// ───────────────────────────────────────────────────────────────────────────

const STATUS_LABELS = {
  brouillon: { label: 'Brouillon', color: '#94a3b8' },
  actif:     { label: 'Actif',     color: '#7B68EE' },
  soumis:    { label: 'Soumis',    color: '#60A5FA' },
  gagne:     { label: 'Gagné',     color: '#4ADE80' },
  perdu:     { label: 'Perdu',     color: '#F87171' },
  archive:   { label: 'Archivé',   color: '#6b7280' },
};

const scoreColor = (s) => {
  if (s == null) return null;
  if (s >= 75) return '#4ADE80';
  if (s >= 50) return '#FACC15';
  if (s >= 25) return '#FB923C';
  return '#F87171';
};

const formatDeadline = (deadline) => {
  if (!deadline) return null;
  const days = Math.floor((new Date(deadline) - new Date()) / 86400000);
  if (days < 0) return { label: `Dépassée ${Math.abs(days)}j`, color: '#F87171' };
  if (days === 0) return { label: "Aujourd'hui", color: '#FB923C' };
  if (days <= 7) return { label: `Dans ${days}j`, color: '#FACC15' };
  return { label: `Dans ${days}j`, color: 'var(--muted2)' };
};

const Shortlists = () => {
  const { user, currentOrg, orgLoading, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && !user) navigate('/login', { replace: true });
  }, [user, authLoading, navigate]);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const data = await tendersAPI.globalShortlists();
      setRows(data);
    } catch (err) {
      toast({ title: 'Erreur', description: 'Impossible de charger les shortlists.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (currentOrg) fetch();
    else if (!authLoading) setLoading(false);
  }, [currentOrg, authLoading, fetch]);

  if (authLoading || orgLoading) {
    return (
      <div style={{ position: 'fixed', inset: 0, background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <style>{`@keyframes sd-spin { to { transform: rotate(360deg); } }`}</style>
        <div style={{ width: 40, height: 40, borderRadius: '50%', border: '2.5px solid var(--border)', borderTopColor: 'var(--vio)', animation: 'sd-spin 1s linear infinite' }} />
      </div>
    );
  }

  // Group by tender_id
  const grouped = {};
  for (const r of rows) {
    if (!grouped[r.tender_id]) {
      grouped[r.tender_id] = {
        tender_id: r.tender_id,
        tender_title: r.tender_title,
        tender_client: r.tender_client,
        tender_status: r.tender_status,
        tender_deadline: r.tender_deadline,
        candidates: [],
      };
    }
    grouped[r.tender_id].candidates.push(r);
  }
  const groups = Object.values(grouped);

  return (
    <AppLayout title="Shortlists" dossiersCount={0}>
      <div className="sd-section-head">
        <div>
          <div className="sd-section-title">Shortlists globales</div>
          <div className="sd-section-sub">
            {loading ? '…' : `${rows.length} consultant${rows.length !== 1 ? 's' : ''} épinglé${rows.length !== 1 ? 's' : ''} sur ${groups.length} AO${groups.length !== 1 ? 's' : ''}`}
          </div>
        </div>
        <button className="sd-btn sd-btn-primary" onClick={() => navigate('/tenders')}>
          <Briefcase size={12} /> Voir les AO
        </button>
      </div>

      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {[1, 2].map(i => (
            <div key={i} className="sd-dash-section" style={{ height: 140 }}>
              <div className="sd-skeleton" style={{ height: '100%' }} />
            </div>
          ))}
        </div>
      ) : groups.length === 0 ? (
        <div className="sd-lib-empty">
          <div className="sd-lib-empty-icon">
            <Star size={32} color="var(--vio2)" />
          </div>
          <div className="sd-lib-empty-title">Aucun consultant en shortlist</div>
          <div className="sd-lib-empty-sub">
            Ouvrez un AO et épinglez les consultants les plus pertinents avec l'icône étoile.
          </div>
          <button className="sd-btn sd-btn-primary" onClick={() => navigate('/tenders')} style={{ margin: '0 auto' }}>
            <Briefcase size={12} /> Parcourir les AO
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {groups.map(g => {
            const status = STATUS_LABELS[g.tender_status] || STATUS_LABELS.brouillon;
            const dl = formatDeadline(g.tender_deadline);
            return (
              <div key={g.tender_id} className="sd-dash-section">
                {/* Tender header */}
                <div
                  className="sd-dash-section-head"
                  onClick={() => navigate(`/tenders/${g.tender_id}`)}
                  style={{ cursor: 'pointer' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
                    <ListChecks size={14} color="var(--vio2)" />
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--white)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {g.tender_title}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--muted2)', marginTop: 2, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {g.tender_client && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}><Building2 size={10} /> {g.tender_client}</span>}
                        <span style={{ color: status.color, fontWeight: 600 }}>{status.label}</span>
                        {dl && <span style={{ color: dl.color, display: 'inline-flex', alignItems: 'center', gap: 3 }}><Calendar size={10} /> {dl.label}</span>}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span className="sd-dash-section-count">{g.candidates.length}</span>
                    <ArrowRight size={14} color="var(--muted2)" />
                  </div>
                </div>

                {/* Candidates list */}
                <div style={{ padding: 0 }}>
                  {g.candidates.map((c) => {
                    const score = c.ai_score != null ? c.ai_score : c.score_keyword;
                    const sc = scoreColor(score);
                    return (
                      <div key={`${c.tender_id}-${c.dossier_id}`}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 10,
                          padding: '10px 18px',
                          borderTop: '1px solid var(--border)',
                          cursor: 'pointer',
                        }}
                        onClick={() => navigate(`/dossiers/${c.dossier_id}/edit`)}
                      >
                        {score != null ? (
                          <div style={{
                            minWidth: 38, height: 26, padding: '0 8px',
                            borderRadius: 13,
                            background: `${sc}1a`, color: sc,
                            border: `1px solid ${sc}55`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 11.5, fontWeight: 700,
                          }}>{Math.round(score)}{c.ai_score != null && <span style={{ fontSize: 8, marginLeft: 2, opacity: 0.7 }}>IA</span>}</div>
                        ) : (
                          <div style={{ width: 38, fontSize: 10, color: 'var(--muted)', textAlign: 'center' }}>—</div>
                        )}
                        <Star size={14} fill="#FACC15" stroke="#FACC15" />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--white)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {c.consultant_name || '—'}
                          </div>
                          <div style={{ fontSize: 11, color: 'var(--muted2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {c.consultant_title || '—'}
                          </div>
                          {c.notes && (
                            <div style={{ fontSize: 10.5, color: 'var(--muted)', marginTop: 3, fontStyle: 'italic' }}>
                              « {c.notes} »
                            </div>
                          )}
                        </div>
                        <ArrowRight size={12} color="var(--muted)" />
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </AppLayout>
  );
};

export default Shortlists;
